import React from 'react';

import { useTheme } from '../../../../theme/ThemeContext';
import ToothChartContainer from '../ToothChartContainer';
import useTabletDentalChartStore from './useTabletDentalChartStore';

/**
 * TabletDentalChartHost — mount point for the chairside dental chart on
 * coarse-pointer devices. Subscribes to `useTabletDentalChartStore`;
 * when the dentist taps the ProcedureWidget's `Q-N · count` chip on a
 * tablet or phone, the store flips `isOpen` and this host mounts
 * `ToothChartContainer` in `presentation="tabletPaired"` so the chart
 * renders alongside the procedure rail (Variant M — desktop-parity
 * mental model with portrait + phone responsive polish).
 *
 * The `📋 Summary · N` button in the chart header toggles
 * `summaryOpen` on the same store; we pass that pair through so the
 * chart can mount the per-tooth `TabletChartSummaryOverlay` overlay.
 *
 * Mounted exactly once per active consultation from `WidgetHost`, as a
 * sibling to `ProcedureWidget`. On desktop / non-dentist branches the
 * store never opens, so this host returns `null` and pays zero cost.
 */
export default function TabletDentalChartHost() {
  const { isDark } = useTheme();
  const isOpen = useTabletDentalChartStore((s) => s.isOpen);
  const visit = useTabletDentalChartStore((s) => s.visit);
  const viewer = useTabletDentalChartStore((s) => s.viewer);
  const close = useTabletDentalChartStore((s) => s.close);
  const summaryOpen = useTabletDentalChartStore((s) => s.summaryOpen);
  const toggleSummary = useTabletDentalChartStore((s) => s.toggleSummary);

  if (!isOpen || !visit) return null;

  return (
    <ToothChartContainer
      visit={visit}
      viewer={viewer}
      onClose={close}
      isDark={isDark}
      presentation="tabletPaired"
      summaryOpen={summaryOpen}
      onSummaryToggle={toggleSummary}
    />
  );
}
